import React from "react";
import { Stack, Box, Typography, Skeleton, keyframes } from "@mui/material";
import { PieChart } from "@mui/x-charts";
import { useTranslation } from "react-i18next";
import { bytesConverter } from "../MemoryProgress/bytesConverter";

interface StorageUsageChartProps {
  used?: number;
  free?: number;
  total?: number;
  loading?: boolean;
}

const StorageUsageChart: React.FC<StorageUsageChartProps> = ({ used = 0, free = 0, total = 0, loading }) => {
  const { t } = useTranslation();

  const isLoading = loading || used === undefined || free === undefined;
  const hasData = !isLoading && (used! >= 0);
  const pulse = keyframes`
  0% { fill: #0000001c; opacity: 1; }
  50% { fill: #0000001c; opacity: 0.5; }
  100% { fill: #0000001c; opacity: 1; }
`;
  return (
    <Box sx={{ position: "relative", width: "260px" }}>
      <Box sx={{
        filter: hasData ? "none" : "blur(3px)",
        transition: "filter 0.3s ease",
      }}>
        {/* Gráfico */}
        {isLoading ? (
          <PieChart
            tooltip={undefined}
            series={[
              {
                startAngle: -90,
                endAngle: 90,
                innerRadius: "160%",
                outerRadius: "100%",
                cornerRadius: 5,
                cx: "50%",
                cy: "100%",
                data: [
                  { value: 1 },
                ],
                valueFormatter: (item) => ``,
              },
            ]}
            margin={{ right: 10, left: 10, top: 0, bottom: 0 }}
            width={300}
            height={160}
            slotProps={{
              legend: { hidden: true },
            }}

            sx={{ "& path": { animation: `${pulse} 2s ease-in-out infinite` } }}
          />
        ) : (
          <PieChart
            series={[
              {
                startAngle: -90,
                endAngle: 90,
                innerRadius: "160%",
                outerRadius: "100%",
                cx: "50%",
                cy: "100%",
                data: [
                  { value: used!, color: "#22638D" },
                  { value: free!, color: "#E9EFF4" },
                ],
                valueFormatter: (item) => `${bytesConverter(item.value, 2)}`,
              },
            ]}
            margin={{ right: 10, left: 10, top: 0, bottom: 0 }}
            width={260}
            height={160}
            slotProps={{
              legend: { hidden: true },
            }}
          />
        )}

        {/* Texto inferior */}
        <Box
          sx={{ margin: 0 }}
          display="flex"
          justifyContent="space-between"
          alignItems="center"
        >
          {/* Espaço em uso */}
          <Box sx={{ display: "flex", flexDirection: "column", alignItems: "flex-start" }}>
            {isLoading ? (
              <>
                <Skeleton width={60} height={20} />
                <Skeleton width={80} height={16} />
              </>
            ) : (
              <>
                <Typography
                  sx={{
                    font: "normal normal 600 14px/21px Segoe UI",
                    color: "#2D2D2D",
                  }}
                >
                  {bytesConverter(used!, 2)}
                </Typography>
                <Typography
                  sx={{
                    font: "normal normal normal 12px/18px Segoe UI",
                    color: "#969696",
                  }}
                >
                  {t("Dashboard.storageInUse")}
                </Typography>
              </>
            )}
          </Box>

          {/* Espaço total */}
          <Box sx={{ display: "flex", flexDirection: "column", alignItems: "flex-end" }}>
            {isLoading ? (
              <>
                <Skeleton width={60} height={20} />
                <Skeleton width={80} height={16} />
              </>
            ) : (
              <>
                <Typography
                  sx={{
                    font: "normal normal 600 14px/21px Segoe UI",
                    color: "#2D2D2D",
                    textAlign: "right",
                  }}
                >
                  {bytesConverter(free!, 2)}
                </Typography>
                <Typography
                  sx={{
                    font: "normal normal normal 12px/18px Segoe UI",
                    color: "#969696",
                  }}
                >
                  {t("Dashboard.freeStorage")}
                </Typography>
              </>
            )}
          </Box>
        </Box>
      </Box>
      {!hasData && (
        <Box
          sx={{
            position: "absolute",
            top: '50%',
            display: "flex",
            alignSelf: "center",
            justifyContent: "center",
          }}
        >
          <Typography
            sx={{
              font: "normal normal 500 14px/21px Segoe UI",
              color: "#2D2D2D",
              textAlign: "center",
            }}
          >
            {t("Dashboard.notFoundgeneralData")}
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default StorageUsageChart;
